import { httpClientMicro } from '@/api-microservices';
import { Config, fetchConfig } from '@/init/app-config';
import { AcesMake } from '@/types/resources/AcesMake';
import { AcesModel } from '@/types/resources/AcesModel';
import { AcesStyle } from '@/types/resources/AcesStyle';
import { AcesSubModel } from '@/types/resources/AcesSubModel';
import { AcesYear } from '@/types/resources/AcesYear';
import { Area, AreaWithGeo, AreaWithRelativeDistance, CityAreas, TargetMarket } from '@/types/resources/ZipcodeLocations';
import { TurboLocation } from '@/types/ServiceProvider';
import { CarWashPaymentIntent } from '@/types/CarWashPaymentIntent';

export default class TurboService {
  config: Config;
  routes = {
    zipcode: (zipcode: string) => `${this.environmentPath()}/turbo/v1/zipcode/find/${zipcode}`,
    zipcodeSearch: () => `${this.environmentPath()}/turbo/v1/zipcode/search`,
    targetMarketCheck: () => `${this.environmentPath()}/turbo/v1/zipcode/target_market`,
    citiesInState: (state: string) => `${this.environmentPath()}/turbo/v1/states/${state}/cities`,
    nearbyActiveAreas: (zipcode: string) => `${this.environmentPath()}/turbo/v1/areas/${zipcode}/with_active_shops`,
    cityAreas: (state: string, city: string) => `${this.environmentPath()}/turbo/v1/states/${state}/cities/${city}`,
    vehicleYears: () => `${this.environmentPath()}/turbo/v1/vehicles/years`,
    vehicleMakes: (year: number) => `${this.environmentPath()}/turbo/v1/vehicles/years/${year}/makes`,
    allVehicleMakes: () => `${this.environmentPath()}/turbo/v1/vehicles/makes`,
    vehicleModels: (year: number, makeID: number) => `${this.environmentPath()}/turbo/v1/vehicles/years/${year}/makes/${makeID}/models`,
    vehicleSubModels: (year: number, makeID: number, modelID: number) => `${this.environmentPath()}/turbo/v1/vehicles/years/${year}/makes/${makeID}/models/${modelID}/sub_models`,
    vehicleTrims: (year: number, makeID: number, modelID: number, subModelID: number) => `${this.environmentPath()}/turbo/v1/vehicles/years/${year}/makes/${makeID}/models/${modelID}/sub_models/${subModelID}/styles`,
    carWashSearch: (zipcode: string, radius: number) => `${this.environmentPath()}/turbo/v1/locations?locationType=optspot&zipcode=${zipcode}&radius=${radius}`,
    carWashPaymentIntent: () => `${this.environmentPath()}/turbo/car-wash-payment-intent`,
    carWashPrice: (userId: number) => `${this.environmentPath()}/turbo/car-wash-price?userID=${userId}`,
    carWashPurchased: () => `${this.environmentPath()}/turbo/car-wash-purchased`
  };

  constructor() {
    this.config = fetchConfig();
  }

  environmentPath() {
    return this.config.get('VUE_APP_TURBO_API_BASE_URL');
  }

  public getZipcode(zipcode: string): Promise<AreaWithGeo> {
    return httpClientMicro.get(this.routes.zipcode(zipcode)).then((resp) => resp.data.data.area);
  }

  public getZipcodeSearch(term: string): Promise<Area[]> {
    return httpClientMicro.get(`${this.routes.zipcodeSearch()}?term=${term}`).then((resp) => resp.data);
  }

  public getTargetMarketCheck(zipcode: string): Promise<TargetMarket> {
    return httpClientMicro.get(`${this.routes.targetMarketCheck()}?zipcode=${zipcode}`).then((resp) => resp.data);
  }

  public getCitiesInState(state: string): Promise<string[]> {
    return httpClientMicro.get(this.routes.citiesInState(state)).then((resp) => resp.data.cities);
  }

  public fetchNearbyAreas(zipcode: string): Promise<AreaWithRelativeDistance[]> {
    return httpClientMicro.get(this.routes.nearbyActiveAreas(zipcode)).then((resp) => resp.data.areas);
  }

  public fetchCityAreas(state: string, city: string): Promise<CityAreas> {
    return httpClientMicro.get(this.routes.cityAreas(state, city)).then((resp) => resp.data);
  }

  public fetchVehcleYears(): Promise<AcesYear[]> {
    return httpClientMicro.get(this.routes.vehicleYears()).then((resp) => resp.data.data.years);
  }

  public fetchVehicleMakes(year: number): Promise<AcesMake[]> {
    return httpClientMicro.get(this.routes.vehicleMakes(year)).then((resp) => resp.data.data.makes);
  }

  public fetchAllVehicleMakes(): Promise<AcesMake[]> {
    return httpClientMicro.get(this.routes.allVehicleMakes()).then((resp) => resp.data.data.makes);
  }

  public fetchVehicleModels(year: number, makeID: number): Promise<AcesModel[]> {
    return httpClientMicro.get(this.routes.vehicleModels(year, makeID)).then((resp) => resp.data.data.models);
  }

  public fetchVehicleSubModels(year: number, makeID: number, modelID: number): Promise<AcesSubModel[]> {
    return httpClientMicro.get(this.routes.vehicleSubModels(year, makeID, modelID)).then((resp) => resp.data.data.sub_models);
  }

  public fetchVehicleTrims(year: number, makeID: number, modelID: number, subModelID: number): Promise<AcesStyle[]> {
    return httpClientMicro.get(this.routes.vehicleTrims(year, makeID, modelID, subModelID)).then((resp) => resp.data.data.styles);
  }

  public getCarWashLocations(zipcode: string): Promise<TurboLocation[]> {
    return httpClientMicro.get(this.routes.carWashSearch(zipcode, 15)).then((resp) => resp.data.data.locations);
  }

  public createCarWashPaymentIntent(userID: number): Promise<CarWashPaymentIntent> {
    return httpClientMicro.post(this.routes.carWashPaymentIntent(), { user_id: userID }).then((resp) => resp.data.data);
  }

  public getCarWashPrice(userID: number): Promise<number> {
    return httpClientMicro.get(this.routes.carWashPrice(userID)).then((resp) => resp.data.data.price);
  }

  public carWashPurchased(userID: number, paymentID: string, phoneNumber: string): Promise<boolean> {
    return httpClientMicro.post(this.routes.carWashPurchased(), { user_id: userID, payment_intent_id: paymentID, phone_number: phoneNumber }).then((resp) => resp.data.success);
  }
}
